import React, { useMemo, useState } from 'react';

import { Flex, Table } from 'antd-v5';
import _, { debounce } from 'lodash';
import { useSelector } from 'react-redux';
import { Search } from 'scouting-ui/lib/Input';

import * as RosterLiteMember from '@modules/roster-lite/models/rosterLiteMember';
import { isMobileSel } from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization';

import styles from './RosterLiteTable.module.scss';
import { getColumns } from './columns';

interface RosterLiteTableProps {
  members: RosterLiteMember.RosterLiteMember[];
  loading?: boolean;
}

export default function RosterLiteTable({
  members,
  loading,
}: RosterLiteTableProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const isMobile = useSelector(isMobileSel);

  const handleSearch = useMemo(
    () => debounce((value: string) => setSearchQuery(value), 300),
    [],
  );

  const filteredMembers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return members;
    return _.chain(members)
      .filter(m => {
        const haystack = [
          RosterLiteMember.getDisplayName(m),
          m.memberId,
          ...m.positions,
        ]
          .join('\0')
          .toLowerCase();
        return haystack.includes(q);
      })
      .orderBy(['firstName'])
      .value();
  }, [members, searchQuery]);

  const positionFilters = useMemo(() => {
    const unique = Array.from(new Set(members.flatMap(m => m.positions))).sort(
      (a, b) => a.localeCompare(b),
    );
    return unique.map(p => ({ text: p, value: p }));
  }, [members]);

  const columns = useMemo(
    () => getColumns(positionFilters, isMobile),
    [positionFilters, isMobile],
  );

  return (
    <Flex vertical gap="small">
      <div className={styles.searchContainer}>
        <Search
          placeholder={intl.formatMessage({
            id: 'rosterLite.table.searchPlaceholder',
          })}
          onChange={e => handleSearch(e.target.value)}
          allowClear
          onClear={() => setSearchQuery('')}
        />
      </div>
      <Table<RosterLiteMember.RosterLiteMember>
        size="small"
        rowKey="personGuid"
        columns={columns}
        dataSource={filteredMembers}
        loading={loading}
        sticky={{ offsetHeader: 60 + 80 }}
        scroll={{ x: 'max-content' }}
        pagination={{ defaultPageSize: 20 }}
      />
    </Flex>
  );
}
